import { Markdown } from '@apps/web/components/ui/markdown';
import { useAgencyContext } from '@apps/web/swr_data_fetching/data_fetching_hooks';

/**
 * The organization's agency context as it stands.
 *
 * Shows the whole document rather than only the fact this note wrote, so the
 * card matches what Settings → Agency context shows.
 */
export function AgencyContextPreview({
    organizationId,
    fallbackText,
}: {
    readonly organizationId: string | null;
    readonly fallbackText: string;
}) {
    const { data, isLoading, error } = useAgencyContext({
        organizationId,
        enabled: true,
    });
    const text = data?.text ?? '';

    if (text === '') {
        // Gated (Agency Guide off for this org) reads as 404, not as
        // genuinely empty: claiming "no agency details recorded" would be
        // false for an org that has facts the flag is just hiding.
        const isGated =
            typeof error === 'object' &&
            error !== null &&
            'status' in error &&
            (error as { readonly status?: number }).status === 404;
        // Loading and genuinely-empty look identical otherwise, and callers
        // with no fallback text render a blank paragraph either way.
        return (
            <p className="text-muted-foreground text-xs leading-4">
                {isLoading
                    ? 'Loading…'
                    : isGated
                      ? fallbackText ||
                        'Agency Guide is off for this organization.'
                      : fallbackText ||
                        'Phoebe has no agency details recorded yet.'}
            </p>
        );
    }

    return (
        <Markdown className="text-foreground/85 text-xs leading-4">
            {text}
        </Markdown>
    );
}
